import React, { useEffect, useMemo, useRef, useState } from "react";
import styled, { css, keyframes } from "styled-components";
import { FiCalendar, FiChevronDown, FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";
import { tokens as T, surface, hairline } from "../../../styles/designTokens";
import { tokens as activity } from "../../activity-logs/styles";
import { SANS } from "../styles";
import { formatMonth } from "../dashboardData";

/**
 * Controles da tela em uma linha, no estilo dos controles do iOS: cápsulas
 * preenchidas, sem borda, com estado ativo tingido de verde. O período usa um
 * seletor próprio (atalhos + grade de meses), no lugar do <input type="month">.
 */

const MONTHS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const dk = (theme) => activity.dk(theme);
const text2 = ({ theme }) => activity.label2(theme);
const capsuleFill = ({ theme }) => (dk(theme) ? "rgba(118,118,128,0.24)" : "rgba(118,118,128,0.12)");
const capsuleHover = ({ theme }) => (dk(theme) ? "rgba(118,118,128,0.32)" : "rgba(118,118,128,0.18)");

// Toque do iOS: o botão encolhe um pouco e perde opacidade ao pressionar
const press = css`
  transition: background-color 180ms ease, color 180ms ease, opacity 120ms ease, transform 160ms cubic-bezier(0.2, 0.9, 0.3, 1.2), box-shadow 180ms ease;
  -webkit-tap-highlight-color: transparent;
  &:active:not(:disabled) { transform: scale(0.97); opacity: 0.72; }
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px ${T.brandRing}; }
  &:disabled { opacity: 0.38; cursor: default; }
  @media (prefers-reduced-motion: reduce) { transition: none; &:active:not(:disabled) { transform: none; } }
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
  font-family: ${SANS};
`;

export const ToolbarDivider = styled.span`
  width: 1px;
  height: 20px;
  margin: 0 2px;
  background: ${({ theme }) => hairline(theme, true)};
  @media (max-width: 720px) { display: none; }
`;

/**
 * Botões no sistema do iOS (UIButton.Configuration):
 * - Capsule: cinza (gray), para campos e ações neutras; com $active fica tingido de verde
 * - TintedCapsule: verde claro com texto verde (tinted), ações secundárias
 * - PrimaryCapsule: verde sólido com texto branco (filled), uma por tela
 * - TintedDanger: vermelho claro (tinted destrutivo)
 * - RoundButton: só ícone, redondo
 * Sem gradientes nem sombras: a hierarquia vem só do preenchimento.
 */
export const Capsule = styled.button`
  height: 34px;
  padding: 0 15px;
  border: none;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: ${SANS};
  font-size: 0.84em;
  font-weight: 600;
  letter-spacing: -0.015em;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  -webkit-font-smoothing: antialiased;
  color: ${({ $active, theme }) => ($active ? T.brand : theme.text)};
  background-color: ${({ $active }) => ($active ? T.brandSoft : capsuleFill)};
  ${press}

  svg { flex-shrink: 0; font-size: 15px; stroke-width: 2.4; opacity: ${({ $active }) => ($active ? 1 : 0.7)}; }
  .chev { font-size: 13px; opacity: 0.45; margin-right: -3px; }
  &:hover:not(:disabled) { background-color: ${({ $active }) => ($active ? T.brandRing : capsuleHover)}; }
`;

export const RoundButton = styled(Capsule)`
  width: 34px;
  padding: 0;
  svg { opacity: 0.8; }
`;

export const TintedCapsule = styled(Capsule)`
  color: ${T.brand};
  background-color: ${T.brandSoft};
  svg { opacity: 1; }
  &:hover:not(:disabled) { background-color: rgba(16, 185, 129, 0.18); }
`;

export const TintedDanger = styled(Capsule)`
  color: #ff453a;
  background-color: rgba(255, 69, 58, 0.13);
  svg { opacity: 1; }
  &:hover:not(:disabled) { background-color: rgba(255, 69, 58, 0.2); }
`;

export const PrimaryCapsule = styled(Capsule)`
  padding: 0 17px;
  color: #fff;
  background-color: ${T.brand};
  svg { opacity: 1; }
  &:hover:not(:disabled) { background-color: #12c48b; }
`;

// Select nativo dentro de uma cápsula: o texto é do select, o chevron é nosso
const SelectCapsule = styled(Capsule).attrs({ as: "label" })`
  position: relative;
  padding-right: 30px;

  select {
    appearance: none;
    -webkit-appearance: none;
    border: none;
    outline: none;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;
    max-width: 190px;
    text-overflow: ellipsis;
    color-scheme: ${({ theme }) => (dk(theme) ? "dark" : "light")};
  }
  option { background: ${({ theme }) => activity.solid(theme)}; color: ${({ theme }) => theme.text}; }
  .chev { position: absolute; right: 12px; pointer-events: none; }
  &:focus-within { box-shadow: 0 0 0 3px ${T.brandRing}; }
`;

export function SelectField({ icon, value, onChange, children, label, active }) {
  return (
    <SelectCapsule $active={active} title={label}>
      {icon}
      <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>{children}</select>
      <FiChevronDown className="chev" />
    </SelectCapsule>
  );
}

// ─── Seletor de período ─────────────────────────────────────────────────────

const pop = keyframes`
  from { opacity: 0; transform: translateY(-4px) scale(0.98); }
  to { opacity: 1; transform: none; }
`;

const Anchor = styled.div`
  position: relative;
`;

const Popover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 60;
  width: 336px;
  padding: 14px;
  border-radius: 18px;
  font-family: ${SANS};
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.text};
  /* Opaco: dentro da TopBar, que já tem backdrop-filter, o blur do popover não funciona */
  background: ${({ theme }) => (dk(theme) ? "#232327" : "#ffffff")};
  border: 1px solid ${({ theme }) => hairline(theme, true)};
  box-shadow: 0 24px 48px -12px rgba(0, 0, 0, 0.45), 0 2px 6px rgba(0, 0, 0, 0.12);
  transform-origin: top right;
  animation: ${pop} 160ms cubic-bezier(0.2, 0.8, 0.2, 1);
  @media (prefers-reduced-motion: reduce) { animation: none; }
  @media (max-width: 520px) { right: auto; left: 0; width: min(336px, calc(100vw - 32px)); }

  .presets { display: grid; grid-template-columns: repeat(6, auto); justify-content: space-between; gap: 4px; }
  .year {
    margin: 14px 0 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.95em;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
  .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px 0; }
  .foot {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${({ theme }) => hairline(theme)};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    font-size: 0.8em;
    color: ${text2};
  }
  .hint { margin-top: 8px; font-size: 0.74em; color: ${text2}; }
`;

const Preset = styled.button`
  height: 28px;
  padding: 0 10px;
  border: none;
  border-radius: 999px;
  font-family: ${SANS};
  font-size: 0.78em;
  white-space: nowrap;
  font-weight: 500;
  cursor: pointer;
  color: ${({ $on, theme }) => ($on ? "#fff" : theme.text)};
  background: ${({ $on }) => ($on ? T.brand : capsuleFill)};
  ${press}
  &:hover:not(:disabled) { background: ${({ $on }) => ($on ? T.brand : capsuleHover)}; }
`;

const NavButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${T.brand};
  background: transparent;
  font-size: 17px;
  ${press}
  &:hover:not(:disabled) { background: ${T.brandSoft}; }
`;

// Célula de mês: a faixa selecionada vira uma pílula contínua, com as pontas cheias
const MonthCell = styled.button`
  position: relative;
  height: 36px;
  border: none;
  padding: 0;
  font-family: ${SANS};
  font-size: 0.84em;
  font-weight: ${({ $edge }) => ($edge ? 600 : 500)};
  cursor: pointer;
  background: ${({ $inRange }) => ($inRange ? T.brandSoft : "transparent")};
  border-radius: ${({ $start, $end }) => `${$start ? "999px" : "0"} ${$end ? "999px" : "0"} ${$end ? "999px" : "0"} ${$start ? "999px" : "0"}`};
  color: ${({ $edge, $hasData, theme }) => {
    if ($edge) return "#fff";
    return $hasData ? theme.text : activity.label2(theme);
  }};
  opacity: ${({ $hasData, $edge }) => ($hasData || $edge ? 1 : 0.55)};
  ${press}
  &:active:not(:disabled) { transform: none; }

  span {
    position: relative;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 32px;
    border-radius: 999px;
    background: ${({ $edge }) => ($edge ? T.brand : "transparent")};
    transition: background 140ms ease;
  }
  &:hover:not(:disabled) span { background: ${({ $edge, theme }) => ($edge ? T.brand : surface(theme, 2))}; }

  /* Ponto discreto nos meses com crédito */
  &::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: 3px;
    width: 3px;
    height: 3px;
    margin-left: -1.5px;
    border-radius: 50%;
    z-index: 2;
    background: ${({ $edge }) => ($edge ? "rgba(255,255,255,0.8)" : T.brand)};
    opacity: ${({ $hasData }) => ($hasData ? 1 : 0)};
  }
`;

const keyOf = (y, m) => `${y}-${String(m + 1).padStart(2, "0")}`;
const shiftMonth = (key, delta) => {
  const [y, m] = key.split("-").map(Number);
  const d = y * 12 + (m - 1) + delta;
  return keyOf(Math.floor(d / 12), d % 12);
};

export const periodText = (from, to) => {
  if (!from && !to) return "Todo o período";
  if (from && to) return from === to ? formatMonth(from) : `${formatMonth(from)} – ${formatMonth(to)}`;
  return from ? `Desde ${formatMonth(from)}` : `Até ${formatMonth(to)}`;
};

/**
 * `available`: meses com crédito ("YYYY-MM"), para marcar a grade e ancorar os
 * atalhos no último mês com dados (a base pode não chegar ao mês atual).
 * A seleção fica em rascunho: o filtro só muda ao fechar a faixa, num atalho ou
 * em Aplicar, para não disparar uma consulta por clique.
 */
export function PeriodPicker({ from, to, onChange, available = [] }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ from, to });
  const anchorRef = useRef(null);
  const availableSet = useMemo(() => new Set(available), [available]);
  const last = available[available.length - 1] || keyOf(new Date().getFullYear(), new Date().getMonth());
  const first = available[0];
  const [year, setYear] = useState(Number((to || from || last).slice(0, 4)));

  useEffect(() => {
    if (!open) return undefined;
    setDraft({ from, to });
    setYear(Number((to || from || last).slice(0, 4)));
    const onDown = (e) => { if (anchorRef.current && !anchorRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); window.removeEventListener("keydown", onKey); };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = (next) => { onChange(next.from || "", next.to || ""); setOpen(false); };

  const lastYear = String(Number(last.slice(0, 4)) - 1);
  const presets = [
    { key: "all", label: "Tudo", value: { from: "", to: "" } },
    { key: "3", label: "3M", value: { from: shiftMonth(last, -2), to: last } },
    { key: "6", label: "6M", value: { from: shiftMonth(last, -5), to: last } },
    { key: "12", label: "12M", value: { from: shiftMonth(last, -11), to: last } },
    { key: "y", label: last.slice(0, 4), value: { from: `${last.slice(0, 4)}-01`, to: last } },
    ...(!first || first <= `${lastYear}-12` ? [{ key: "py", label: lastYear, value: { from: `${lastYear}-01`, to: `${lastYear}-12` } }] : []),
  ];

  const pick = (key) => {
    if (!draft.from || draft.to) { setDraft({ from: key, to: "" }); return; }
    if (key < draft.from) { setDraft({ from: key, to: "" }); return; }
    apply({ from: draft.from, to: key });
  };

  const lo = draft.from;
  const hi = draft.to || draft.from;
  const active = !!(from || to);
  const minYear = Number((first || last).slice(0, 4));
  const maxYear = Number(last.slice(0, 4));

  return (
    <Anchor ref={anchorRef}>
      <Capsule type="button" $active={active} onClick={() => setOpen((v) => !v)} aria-haspopup="dialog" aria-expanded={open}>
        <FiCalendar />
        {periodText(from, to)}
        <FiChevronDown className="chev" />
      </Capsule>

      {open && (
        <Popover role="dialog" aria-label="Selecionar período">
          <div className="presets">
            {presets.map((p) => (
              <Preset key={p.key} type="button" $on={(p.value.from || "") === (from || "") && (p.value.to || "") === (to || "")} onClick={() => apply(p.value)}>
                {p.label}
              </Preset>
            ))}
          </div>

          <div className="year">
            <NavButton type="button" aria-label="Ano anterior" onClick={() => setYear((y) => y - 1)} disabled={year <= minYear}><FiChevronLeft /></NavButton>
            <span>{year}</span>
            <NavButton type="button" aria-label="Próximo ano" onClick={() => setYear((y) => y + 1)} disabled={year >= maxYear}><FiChevronRight /></NavButton>
          </div>

          <div className="grid">
            {MONTHS.map((label, m) => {
              const key = keyOf(year, m);
              const inRange = !!lo && key >= lo && key <= hi;
              const col = m % 4;
              return (
                <MonthCell
                  key={key}
                  type="button"
                  $hasData={availableSet.has(key)}
                  $inRange={inRange && lo !== hi}
                  $edge={key === lo || key === hi}
                  $start={key === lo || col === 0}
                  $end={key === hi || col === 3}
                  onClick={() => pick(key)}
                  aria-pressed={inRange}
                  title={availableSet.has(key) ? `${label}/${year} · com crédito` : `${label}/${year}`}
                >
                  <span>{label}</span>
                </MonthCell>
              );
            })}
          </div>

          <div className="hint">
            {draft.from && !draft.to ? "Agora o mês final, ou aplique só este." : "Escolha o mês inicial e depois o final."}
            {" "}Atalhos a partir de {formatMonth(last)}, último mês com crédito.
          </div>

          <div className="foot">
            <span>{periodText(draft.from, draft.to)}</span>
            <div style={{ display: "flex", gap: 6 }}>
              {(draft.from || draft.to) && (
                <Preset type="button" onClick={() => setDraft({ from: "", to: "" })}><FiX style={{ marginRight: 4, verticalAlign: -1 }} />Limpar</Preset>
              )}
              <Preset type="button" $on onClick={() => apply({ from: draft.from, to: draft.to || draft.from })}>Aplicar</Preset>
            </div>
          </div>
        </Popover>
      )}
    </Anchor>
  );
}
